import axios from 'axios';
import { createClient } from "@supabase/supabase-js";
import { Navigate, Outlet } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Loading from '../components/Loading';
import { addEmailToSubscription, removeEmailFromSubscription } from "../controllers/MailController";

const backendBaseUrl = process.env.REACT_APP_BACKEND_URL;
const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_ANON_KEY);

async function loginAccount(dataLogin, navigate){
    const {data, error} = await supabase.auth.signInWithPassword({
        email: dataLogin.email,
        password: dataLogin.password
    });

    if (error) {
        throw new Error(error.message); // Ensure errors are thrown
    }

    navigate("/");
}

async function logoutAccount(navigate){
    const {data, error} = await supabase.auth.signOut();

    if (error) {
        throw new Error(error.message); // Ensure errors are thrown
    }

    navigate("/");
}

async function updateAccountEmail(data, t){
    try {
        const { dataReturn, error } = await supabase.auth.signInWithPassword({
            email: data.currentEmail, // User's email
            password: data.password, // Verify current password
        });

        if (error) {
            return({ success: false, message: t("toast.passwordIncorrect") });
        }

        const { error: updateError } = await supabase.auth.updateUser({
            email: data.newEmail,
        });

        if (updateError) {
            return({ success: false, message: t("toast.emailUpdateError") });
        }

        return({ success: true, message: t("toast.emailUpdateSuccess") });
    }
    catch (error){
        console.log(error);
    }
}

async function updateAccountPassword(data, t){
    try {
        const { dataReturn, error } = await supabase.auth.signInWithPassword({
            email: data.email, // User's email
            password: data.currentPassword, // Verify current password
          });
        
          if (error) {
            return({ success: false, message: t("toast.currentPasswordIncorrect") });
          }
        
          // Step 2: Update the password if reauthentication is successful
          const { error: updateError } = await supabase.auth.updateUser({
              password: data.newPassword,
          });
          if (updateError) {
            return({ success: false, message: t("toast.passwordUpdateError") });
          }
        
          return({ success: true, message: t("toast.passwordUpdateSuccess") });
    }
    catch (error){
        console.log(error);
    }
}

async function updateUserPreferences(userId, userEmail, subscribeNewsletter, enableTwoFactor, enableFantasyMode, t){
    try {
        const response = await axios.post(backendBaseUrl + 'account/updatePreferences', {
          userId,
          subscribeNewsletter,
          enableTwoFactor,
          enableFantasyMode
        });

        if (subscribeNewsletter)
            await addEmailToSubscription(userEmail);
        else 
            await removeEmailFromSubscription(userEmail);

        return response.data;
      } catch (error) {
        console.error('Error calling backend to update preferences:', error.message);
        return {
          success: false,
          message: t("errors.preferencesUpdateError")
        };
      }
}

async function resetPassword(email, t){
    const {dataReturn, error} = await supabase.auth.resetPasswordForEmail(email);

    if (error) {
        return({success: false, message: t("errors.resetPasswordError")});
    }

    return({success: true, message:  t("errors.resetPasswordSuccess")});
}

async function updateForgottenUserPassword(newPassword, t) {

    const {dataReturn, error} = await supabase.auth.updateUser({
        password: newPassword
    });

    if (error) {
        return({success: false, message: t("errors.forgotPasswordError")});
    }

    return({success: true, message: t("errors.forgotPasswordSuccess")});
}

async function setSessionToken(data, navigate){
    await supabase.auth.setSession({access_token: data.access_token, refresh_token: data.refresh_token})
}

async function createAccount(data){
    try {
        const response = await axios.post(backendBaseUrl + "account/create", data);
    }
    catch (error){
        console.log(error);
    }
}

async function getUserFromBD(){
    try {
        const { data: { user } } = await supabase.auth.getUser();
        return user;
    }
    catch (error){
        console.log(error);
    }
}

const subscribeToAuthChanges = (setUser) => {
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
        setUser(session?.user || null);
            if (_event === "SIGNED_IN") {
                addUserDataToBD(session?.user);
            }
    });

    return () => {
        listener.subscription.unsubscribe();
    };
};

async function updateAccount(data){
    try {
        const response = await axios.post(backendBaseUrl + "account/update", data);
    }
    catch (error){
        console.log(error);
    }
}

async function getOrderHistory(){
    try {
        const { data: user, error: authError } = await supabase.auth.getUser();
        if (authError) {
            console.error("Error fetching user:", authError);
            throw new Error(authError.message);  // Throw the error so it can be caught
        }

    const response = await axios.post(backendBaseUrl + 'account/getOrderHistory', {
        userId: user.user.id
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching order history from backend:', error.message);
    return {
      success: false,
      message: 'Error while getting orders'
    };
  }
}

async function addUserDataToBD(data) {
    try {
        // Get the currently authenticated user
        const { data: user, error: authError } = await supabase.auth.getUser();
        if (authError) {
            console.error("Error fetching user:", authError);
            throw new Error(authError.message);  // Throw the error so it can be caught
        }

        const response = await axios.post(backendBaseUrl + 'account/addUserData', {
          userId: user.user.id,
          email: data.email
        });
    
        // if (response.data.success) {
        //   console.log('Stripe ID:', response.data.stripeCustomerId);
        // } else {
        //   console.warn(response.data.message);
        // }
      } catch (error) {
        console.error('Error adding user data to backend:', error.message);
      }
}

async function fetchUserDataFromBd(userId) {
    try {
        const response = await axios.post(backendBaseUrl + 'account/getUserData', {
          userId
        });
    
        if (response.data.success) {
          return response.data.userData;
        } else {
          console.warn(response.data.message);
          return null;
        }
      } catch (error) {
        console.error('Error fetching user data from backend:', error.message);
        return null;
      }
}

async function fetchStripeUserData(stripeId, t) {
    try {
        const data = {stripeId};
        const response = await axios.post(backendBaseUrl + "account/getStripeCustomer", data);
        return(response);
    }
    catch (error){
        return({ success: false, message: t("errors.stripeUserDataError") });
    }
}

async function setDefaultShippingAddressWithKey(addressKey, stripe_id, t){
    try {
        const data = {addressKey, stripe_id};
        const response = await axios.post(backendBaseUrl + "account/updateStripeShippingAddressWithKey", data);
        
        return({ success: true, message: t("errors.defaultAddressSuccess"), data: response });
    }
    catch (error){
        return({ success: false, message: t("errors.defaultAddressError") });
    }
}

async function updateShippingAddressWithKey(addressKey, firstName, lastName, phone, line1, line2, city, country, province, zip, stripe_id, t){
    try {
        const data = {addressKey, firstName, lastName, phone, line1, line2, city, country, province, zip, stripe_id};
        const response = await axios.post(backendBaseUrl + "account/updateStripeShippingAddressDataWithKey", data);
        
        return({ success: true, message: t("errors.updateAddressSuccess"), data: response });
    }
    catch (error){
        return({ success: false, message: t("errors.updateAddressError") });
    }
}

async function addShippingAddressBD(firstName, lastName, phone, line1, line2, city, country, province, zip, email, stripe_id, t){
    try {
        const data = {firstName, lastName, phone, line1, line2, city, country, province, zip, email, stripe_id};
        const response = await axios.post(backendBaseUrl + "account/addStripeShippingAddress", data);

        if (response.data.success == false){
            return({ success: false, message: t("errors.addAddressMaxError") });
        }

        return({ success: true, message: t("errors.addAddressSuccess"), data: response });
    }
    catch (error){
       
        return({ success: false, message: t("errors.addAddressError") });
    }
}

async function removeAddressAtKey(key, stripe_id, t){
    try {
        const data = {key, stripe_id}
        const response = await axios.post(backendBaseUrl + "account/removeAddress", data)

        return({ success: true, message: t("errors.removeAddressSuccess"), data: response });
    }
    catch (error){
        return({ success: false, message: t("errors.removeAddressError") });
    }
}

async function saveOrderToBD(paymentIntent, metadata, t){
    try {
        // Get the currently authenticated user
        const { data: userData, error: authError } = await supabase.auth.getUser();
        const userId = userData?.user?.id || null; // Use 0 for guest

        // Save order regardless of whether user is logged in
        const response = await axios.post(backendBaseUrl + 'account/saveOrder', {
            userId,
            paymentIntent,
            metadata
        });

        return response.data;
      } catch (error) {
        console.error("Error saving order:", error.message);
        return {
          success: false,
          message: t("errors.orderSaveError")
        };
      }
}

async function fetchOrderFromBD(stripePaiementId, t) {
    try {
        const { data: user, error: authError } = await supabase.auth.getUser();
        const userId = user?.user?.id || null;

        const response = await axios.post(backendBaseUrl + 'account/getOrder', {
          stripePaiementId,
          userId
        });
    
        return response.data;
      } catch (error) {
        console.error('Error fetching order from backend:', error.message);
        return {
          success: false,
          message: t("errors.orderGetError")
        };
      }
}

async function fetchStripeOrder(stripePaiementId){
    const data = {stripePaiementId};
    try {
        const response = await axios.post(backendBaseUrl + "account/order", data);
        return response.data.paymentInfo;
    }
    catch (error){
        console.log(error);
    }
}

const ProtectedRoute = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    };
    checkUser();
  }, []);

  if (isAuthenticated === null) {
    return <Loading isVisible={true}/>; // Show loading until auth status is known
  }
  
return isAuthenticated ? <Outlet /> : <Navigate to="/account/login" replace />;
};

export default ProtectedRoute;

export {ProtectedRoute, fetchStripeOrder, fetchOrderFromBD, saveOrderToBD, updateShippingAddressWithKey, setDefaultShippingAddressWithKey, removeAddressAtKey, addShippingAddressBD, updateUserPreferences, addUserDataToBD, fetchStripeUserData, fetchUserDataFromBd, setSessionToken, resetPassword, updateForgottenUserPassword, updateAccountEmail, updateAccountPassword, createAccount, updateAccount, getOrderHistory, loginAccount, getUserFromBD, subscribeToAuthChanges, logoutAccount };